/* 🎡 عجلة الحظ — تجميع أرقام واتساب من الزوار الجدد مقابل جائزة خصم */
import React, { useEffect, useRef, useState } from "react";
import { X, Gift, Copy, Check, Loader2 } from "lucide-react";
import http from "@/api";
import { useApp } from "@/context";
import { toast } from "sonner";

export default function SpinWheel() {
  const { lang } = useApp();
  const ar = lang === "ar";
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (localStorage.getItem("rg_spin_done")) return;
    http.get("/public/spin-wheel").then(({ data }) => {
      if (data.enabled && (data.prizes || []).length) {
        setCfg(data);
        if (!sessionStorage.getItem("rg_spin_seen")) {
          sessionStorage.setItem("rg_spin_seen", "1");
          timerRef.current = setTimeout(() => setOpen(true), 6000);
        }
      }
    }).catch(() => {});
    return () => clearTimeout(timerRef.current);
  }, []);

  if (!cfg) return null;

  const prizes = cfg.prizes || [];
  const seg = 360 / prizes.length;
  const gradient = prizes.map((p, i) =>
    `${p.color || "#16a34a"} ${i * seg}deg ${(i + 1) * seg}deg`).join(", ");

  const spin = async () => {
    const ph = phone.trim();
    if (ph.replace(/\D/g, "").length < 8) {
      toast.error(ar ? "أدخل رقم واتساب صحيحاً" : "Enter a valid WhatsApp number");
      return;
    }
    setSpinning(true);
    try {
      const { data } = await http.post("/public/spin", { phone: ph });
      localStorage.setItem("rg_spin_done", "1");
      const idx = data.prize_index || 0;
      // 5 لفات كاملة + الهبوط على منتصف الجائزة (المؤشر أعلى العجلة)
      const target = 360 * 5 + (360 - (idx * seg + seg / 2));
      setRotation(target);
      setTimeout(() => {
        setResult(data);
        setSpinning(false);
      }, 4200);
    } catch (e) {
      setSpinning(false);
      toast.error(e?.response?.data?.detail || (ar ? "تعذر اللف — جرب لاحقاً" : "Spin failed"));
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(result?.coupon_code || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      {!open && !localStorage.getItem("rg_spin_done") && (
        <button onClick={() => setOpen(true)}
                className="fixed bottom-24 start-3 z-40 w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white animate-bounce"
                style={{ background: "linear-gradient(135deg,#d97706,#dc2626)" }}
                data-testid="spin-wheel-fab" aria-label="spin wheel">
          <Gift size={22} />
        </button>
      )}
      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
             style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}
             data-testid="spin-wheel-modal">
          <div className="w-full max-w-sm rounded-3xl p-5 text-center relative"
               style={{ background: "var(--bg, #fff)" }}>
            <button onClick={() => setOpen(false)} className="absolute top-3 end-3 opacity-50 hover:opacity-100"
                    data-testid="spin-wheel-close"><X size={18} /></button>
            <div className="text-lg font-bold mb-1">
              🎁 {ar ? "جرّب حظك واربح خصماً!" : "Spin & win a discount!"}
            </div>
            <div className="text-xs opacity-60 mb-4">
              {ar ? "لفة واحدة لكل رقم واتساب" : "One spin per WhatsApp number"}
            </div>

            {/* العجلة */}
            <div className="relative w-56 h-56 mx-auto mb-4">
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-10 text-2xl">🔻</div>
              <div className="w-full h-full rounded-full border-4 border-amber-500 relative overflow-hidden"
                   style={{
                     background: `conic-gradient(${gradient})`,
                     transform: `rotate(${rotation}deg)`,
                     transition: spinning || rotation ? "transform 4s cubic-bezier(0.15, 0.9, 0.25, 1)" : "none",
                   }}
                   data-testid="spin-wheel-disc">
                {prizes.map((p, i) => (
                  <div key={i} className="absolute inset-0 flex items-start justify-center"
                       style={{ transform: `rotate(${i * seg + seg / 2}deg)` }}>
                    <span className="text-[10px] font-bold text-white mt-3 max-w-[70px] leading-tight"
                          style={{ textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}>
                      {ar ? p.label_ar : p.label_en}
                    </span>
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 m-auto w-10 h-10 rounded-full bg-white border-2 border-amber-500 flex items-center justify-center text-lg">🌹</div>
            </div>

            {result ? (
              <div className="space-y-2" data-testid="spin-wheel-result">
                <div className="text-xl font-bold">
                  {result.coupon_code ? "🎉" : "🌸"} {ar ? result.label_ar : result.label_en}
                </div>
                {result.already_claimed && (
                  <div className="text-[11px] opacity-60">{ar ? "سبق أن لفيت بهذا الرقم — هذي جايزتك" : "Already claimed with this number"}</div>
                )}
                {result.coupon_code ? (
                  <button onClick={copyCode}
                          className="mx-auto flex items-center gap-2 px-4 py-2 rounded-full font-mono font-bold text-sm"
                          style={{ background: "#fef3c7", color: "#92400e", border: "1px dashed #d97706" }}
                          data-testid="spin-wheel-code">
                    {result.coupon_code} {copied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                ) : (
                  <div className="text-xs opacity-60">{ar ? "حظ أوفر المرة الجاية 🌹" : "Better luck next time 🌹"}</div>
                )}
                {result.coupon_code && (
                  <div className="text-[11px] opacity-60">{ar ? "استخدم الكود بخانة كود الخصم عند الدفع" : "Use it at checkout"}</div>
                )}
                <button onClick={() => setOpen(false)} className="btn-primary w-full !py-2 text-sm mt-1"
                        data-testid="spin-wheel-done">{ar ? "تم — أكمل التسوق" : "Done"}</button>
              </div>
            ) : (
              <div className="space-y-2">
                <input className="input-field text-center" dir="ltr" inputMode="tel"
                       placeholder={ar ? "رقم الواتساب (974XXXXXXXX)" : "WhatsApp number"}
                       value={phone} onChange={(e) => setPhone(e.target.value)}
                       data-testid="spin-wheel-phone" />
                <button onClick={spin} disabled={spinning}
                        className="btn-primary w-full !py-2.5 font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="spin-wheel-spin-btn">
                  {spinning ? <Loader2 size={16} className="animate-spin" /> : "🎡"}
                  {spinning ? (ar ? "بالتوفيق..." : "Good luck...") : (ar ? "لف العجلة!" : "Spin!")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
